import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '@/services';
import type { RouteAssignment } from '@/types';
import { getPayPeriod, getReportPeriod, notifyLiveDataError } from '@/utils';

/**
 * Subscribes to the full date envelope for an ending month, across all users.
 * Query spans both schedules: from previous month's 21st through selected
 * month's final day. Reports filter results using each user's assigned schedule.
 * A single range query with client aggregation remains live via `onSnapshot`.
 */
export const useMonthlyAssignments = (periodMonth: string) => {
  const [assignments, setAssignments] = useState<RouteAssignment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const { startDate } = getPayPeriod(periodMonth);
    const { endDateExclusive } = getReportPeriod('calendar-month', periodMonth);

    const q = query(
      collection(db, 'assignments'),
      where('date', '>=', startDate),
      where('date', '<', endDateExclusive),
      orderBy('date', 'asc'),
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setAssignments(
          snapshot.docs.map((docSnap) => ({
            ...(docSnap.data() as RouteAssignment),
            id: docSnap.id,
          })),
        );
        setLoading(false);
      },
      (error) => {
        notifyLiveDataError('useMonthlyAssignments', error);
        setLoading(false);
      },
    );
    return unsubscribe;
  }, [periodMonth]);

  return { assignments, loading };
};
